import type { FreezeReason, RowFreeze } from '@signalvote/shared';

/** What a contribution or expenditure ledger row offers, read off the server's `freeze` verdict. */
export interface MoneyRowAffordances {
  /** Field editors, and the debt and loan tags that save through the same update. */
  edit: boolean;
  /** Exclude, which voids the row. */
  void: boolean;
  /** Restore, which un-voids the row. */
  unvoid: boolean;
  /** The permanent Delete, and Undo accept, which deletes. */
  delete: boolean;
  /** Which lock the banner explains. A voided row is not locked, it is excluded. */
  lock: Exclude<FreezeReason, 'Voided'> | null;
}

/**
 * Mirrors the server guards, except that Exclude stays hidden on a row on the portal: the server
 * accepts that void and records drift, but the ledger has never offered it. Update refuses on
 * every reason. Restore refuses only a calendar lock. Delete accepts a voided row. Delete also refuses a row behind a transmitted report in an open period
 * (ADR-081); `freeze` does not carry that, so the delete refusal names the report instead.
 */
export function moneyRowAffordances(freeze: RowFreeze): MoneyRowAffordances {
  if (freeze.kind === 'Editable') return { edit: true, void: true, unvoid: false, delete: true, lock: null };
  const has = (reason: FreezeReason) => freeze.reasons.includes(reason);
  const calendarLocked = has('CalendarLocked');
  const uploaded = has('UploadedToPortal');
  return {
    edit: false,
    void: !calendarLocked && !has('Voided') && !uploaded,
    unvoid: !calendarLocked && has('Voided'),
    delete: !calendarLocked && !uploaded,
    lock: uploaded ? 'UploadedToPortal' : calendarLocked ? 'CalendarLocked' : null,
  };
}
